import { useEffect, useRef } from 'react';
import type { ServerToClientEvents } from '@adco-rooms/shared';
import { useRealtimeContext, type RealtimeContextValue } from '@/lib/realtime/realtime-provider';

/** Connection state + room subscription helpers. */
export function useRealtime(): RealtimeContextValue {
  return useRealtimeContext();
}

/**
 * Subscribes a screen to the realtime channel of the rooms it is displaying.
 * The subscription is refreshed whenever the visible room set changes and is
 * released on unmount.
 */
export function useRoomSubscription(roomIds: readonly number[]): void {
  const { subscribeRooms, unsubscribeRooms, status } = useRealtimeContext();
  const key = roomIds.join(',');
  const previousRef = useRef<readonly number[]>([]);

  useEffect(() => {
    const ids = key ? key.split(',').map(Number) : [];
    const previous = previousRef.current;

    const removed = previous.filter((id) => !ids.includes(id));
    if (removed.length > 0) unsubscribeRooms(removed);
    if (ids.length > 0) subscribeRooms(ids);

    previousRef.current = ids;
    // `status` re-runs the effect after a reconnection so the server re-joins us.
  }, [key, status, subscribeRooms, unsubscribeRooms]);

  useEffect(
    () => () => {
      if (previousRef.current.length > 0) unsubscribeRooms(previousRef.current);
    },
    [unsubscribeRooms],
  );
}

interface LooseEmitter {
  on(event: string, listener: (...args: unknown[]) => void): void;
  off(event: string, listener: (...args: unknown[]) => void): void;
}

/**
 * Listens to a single typed server event.
 * Prefer query invalidation (already handled globally); use this only for UI
 * side effects such as toasts or highlighting a block that just changed.
 */
export function useRealtimeEvent<TEvent extends keyof ServerToClientEvents>(
  event: TEvent,
  handler: ServerToClientEvents[TEvent],
): void {
  const { socket } = useRealtimeContext();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!socket) return;

    // Stable proxy so re-renders never detach the listener.
    const listener = (...args: unknown[]): void => {
      (handlerRef.current as (...params: unknown[]) => void)(...args);
    };

    // socket.io's typed emitter cannot resolve a listener signature from an
    // unresolved generic key, so we narrow it to a plain string emitter here.
    const emitter = socket as unknown as LooseEmitter;
    emitter.on(event, listener);
    return () => {
      emitter.off(event, listener);
    };
  }, [socket, event]);
}
